import {
  Badge,
  Link,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { type ComponentProps, forwardRef, type ReactNode } from "react";
import { useMenuCondensed } from "../../hooks/useMenuCondensed";

interface NavLinkItemProps {
  icon: ReactNode;
  label: string;
  sx?: ComponentProps<typeof Link>["sx"];
  /** Optional badge content (typically a number) displayed on the right side */
  badge?: ReactNode;
}

/**
 * A navigation link item with an icon and label
 * Designed to be used with routing libraries like React Router or TanStack Router
 */
export const NavLinkItem = forwardRef<HTMLAnchorElement, NavLinkItemProps>(
  ({ icon, label, badge, sx: sxProp, ...otherProps }, ref) => {
    const isCondensed = useMenuCondensed();

    const button = (
      <ListItemButton
        ref={ref}
        component={Link}
        sx={{
          ...(isCondensed ? { justifyContent: "center", px: 2 } : sxProp),
        }}
        {...otherProps}
      >
        <ListItemIcon sx={isCondensed ? { minWidth: "auto" } : undefined}>
          {icon}
        </ListItemIcon>
        {!isCondensed && (
          <>
            <ListItemText
              primary={label}
              slotProps={{ primary: { noWrap: true } }}
            />
            {badge !== undefined && (
              <Badge
                badgeContent={badge}
                sx={{
                  "& .MuiBadge-badge": {
                    // Override default absolute positioning to display inline
                    position: "static",
                    transform: "none",
                    backgroundColor: "error.light",
                    color: "error.contrastText",
                  },
                }}
              />
            )}
          </>
        )}
      </ListItemButton>
    );

    return (
      <ListItem disablePadding>
        {isCondensed ? (
          <Tooltip title={label} placement="right">
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  }
);

NavLinkItem.displayName = "NavLinkItem";
